/**
 * @voltxampere/web — `<vxa-speed-control>` (Sprint 53)
 *
 * Topbar speed dropdown (VISION §6.E, vision-audit Item 19). Renders
 * the active playback speed in a brass-bordered chip; click opens a
 * vertical preset list (0.1× / 0.25× / 0.5× / 1× / 2× / 5× / 10×) with
 * the active value highlighted in brass-hot. Clicking outside or
 * pressing Escape closes the menu.
 *
 * Selection routes through `setPlaybackSpeed` (apps/web store) which
 * snaps + persists via mergeSettings. The runtime `runTransient`
 * boundary (Sprint 15 transient-runner.ts) consumes the resulting
 * `playbackSpeedSignal` value at each call.
 *
 * Bookmark restore (Sprint 32-3 bookmarks.ts) is wired separately —
 * jumping to a bookmark with a stored `speed` field calls
 * `setPlaybackSpeed` and the dropdown reflects the restore via the
 * SignalController.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html } from "lit";
import { customElement } from "lit/decorators.js";

import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import {
  formatSpeedLabel,
  playbackSpeedSignal,
  setPlaybackSpeed,
  SPEED_PRESETS,
} from "../store/playback-speed.js";

@customElement("vxa-speed-control")
export class VxaSpeedControl extends LitElement {
  private readonly _speed = new SignalController(this, playbackSpeedSignal);
  private _open = false;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this._onDocumentClick);
    document.addEventListener("keydown", this._onKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this._onDocumentClick);
    document.removeEventListener("keydown", this._onKeydown);
  }

  static override styles = css`
    :host {
      position: relative;
      display: inline-flex;
    }

    .trigger {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--brass);
      border: 1px solid var(--brass-faint);
      padding: 4px 10px;
      cursor: pointer;
      background: transparent;
      min-width: 56px;
      min-height: 44px;
      text-align: center;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover),
        color var(--transition-hover);
    }

    .trigger:hover,
    .trigger[aria-expanded="true"] {
      background: var(--brass-ghost);
      color: var(--brass-hot);
      border-color: var(--brass);
    }

    .trigger:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -2px;
    }

    .menu {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      display: flex;
      flex-direction: column;
      min-width: 84px;
      padding: 4px 0;
      margin: 0;
      list-style: none;
      background: var(--ink);
      border: 1px solid var(--brass-faint);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
      z-index: 100;
    }

    /* Sprint 53 — always-rendered menu pattern. The shadow tree
       keeps the <ul> in place across opens/closes so Lit's template
       reconciliation never has to swap the element structure. The
       hidden attribute toggles visibility; the .menu[hidden] block
       below restores display:none which our display:flex above
       otherwise overrides. */
    .menu[hidden] {
      display: none;
    }

    .item {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-dim);
      padding: 8px 14px;
      min-height: 44px;
      cursor: pointer;
      text-align: right;
      border: none;
      background: transparent;
      transition: color 120ms ease;
    }

    .item:hover {
      color: var(--ivory);
      background: var(--ink-soft, rgba(255, 255, 255, 0.03));
    }

    .item[aria-current="true"] {
      color: var(--brass-hot);
    }

    .item:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -2px;
    }
  `;

  override render() {
    const active = this._speed.value;
    const presetItems = this._open
      ? SPEED_PRESETS.map(
          (speed) => html`
            <li>
              <button
                class="item"
                type="button"
                role="option"
                aria-current=${speed === active ? "true" : "false"}
                aria-selected=${speed === active ? "true" : "false"}
                data-testid=${`speed-preset-${String(speed)}`}
                data-speed=${String(speed)}
                @click=${() => {
                  this._pick(speed);
                }}
              >
                ${formatSpeedLabel(speed)}
              </button>
            </li>
          `,
        )
      : null;
    return html`
      <button
        class="trigger"
        type="button"
        aria-haspopup="listbox"
        aria-expanded=${this._open ? "true" : "false"}
        aria-label=${t("speed.label")}
        title=${t("speed.label")}
        data-testid="speed-trigger"
        @click=${this._toggle}
      >
        ${formatSpeedLabel(active)}
      </button>
      <ul
        class="menu"
        role="listbox"
        data-testid="speed-menu"
        ?hidden=${!this._open}
        aria-hidden=${this._open ? "false" : "true"}
      >
        ${presetItems}
      </ul>
    `;
  }

  private _toggle = (event: MouseEvent): void => {
    event.stopPropagation();
    this._setOpen(!this._open);
  };

  private _pick(speed: number): void {
    setPlaybackSpeed(speed);
    this._setOpen(false);
  }

  private _onDocumentClick = (event: MouseEvent): void => {
    if (!this._open) return;
    const path = event.composedPath();
    if (!path.includes(this)) {
      this._setOpen(false);
    }
  };

  private _onKeydown = (event: KeyboardEvent): void => {
    if (this._open && event.key === "Escape") {
      this._setOpen(false);
      event.stopPropagation();
    }
  };

  private _setOpen(open: boolean): void {
    if (this._open === open) return;
    this._open = open;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-speed-control": VxaSpeedControl;
  }
}
